import { z } from 'zod';
import { DateTime, Duration } from 'luxon';

const isValidDuration = (val: unknown): val is Duration<true> =>
  Duration.isDuration(val) && val.isValid;
const isValidDateTime = (val: unknown): val is DateTime<true> =>
  DateTime.isDateTime(val) && val.isValid;

const dateObject = z.object({
  year: z.number().min(1000).max(9999),
  month: z.number().min(1).max(12),
  day: z.number().min(1).max(31),

  // We'll probably never use these properties:
  // ordinal: z.number().min(1).max(366),
  // weekYear: z.number(),
  // localWeekYear: z.number(),
  // weekNumber: z.number().min(1).max(53),
  // localWeekNumber: z.number().min(1).max(53),
  // weekDay: z.number().min(1).max(7),
  // localWeekDay: z.number().min(1).max(7),
});

const dateTimeObject = z.object({
  ...dateObject.shape,
  hour: z.number().min(0).max(23).optional(),
  minute: z.number().min(0).max(59).optional(),
  second: z.number().min(0).max(59).optional(),
  millisecond: z.number().min(0).max(999).optional(),
});

const dateTime = z.custom<DateTime>(DateTime.isDateTime, {
  params: { name: 'DateTime' },
});
const validDateTime = dateTime.refine(isValidDateTime);

const stringToDateTime = z.iso
  .datetime({ offset: true })
  .or(z.iso.date())
  .transform((str) => DateTime.fromISO(str));
const dateToDateTime = z.date().transform((date) => DateTime.fromJSDate(date));
const numberToDateTime = z.number().transform((num) => DateTime.fromMillis(num));
const objectToDateTime = dateTimeObject.transform((obj) => DateTime.fromObject(obj));

const dateTimeToDate = dateTime.transform((dateTime) => dateTime.toJSDate());

export const DateTimeSchema = z
  .union([dateTime, stringToDateTime, dateToDateTime, numberToDateTime, objectToDateTime])
  .pipe(dateTime);
export const ValidDateTime = DateTimeSchema.pipe(validDateTime);
export type ValidDateTime = DateTime<true>;

export const DateSchema = DateTimeSchema.pipe(dateTimeToDate);

export const DateString = ValidDateTime.transform((date) => date.toISO()).pipe(
  z.iso.datetime({ offset: true })
);

const DateTimeUnit = z.enum([
  'year',
  'quarter',
  'month',
  'week',
  'day',
  'hour',
  'minute',
  'second',
  'millisecond',
]);
export type DateTimeUnit = z.infer<typeof DateTimeUnit>;

// All units usable in a DurationLike object
export const DurationUnit = z.enum([
  'years',
  'quarters',
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
  ...DateTimeUnit.options,
]);
export type DurationUnit = z.infer<typeof DurationUnit>;

export const CalendarDurationUnit = DurationUnit.extract(['years', 'months', 'weeks', 'days']);
export type CalendarDurationUnit = z.infer<typeof CalendarDurationUnit>;

export const DurationLikeObject = z
  .partialRecord(DurationUnit, z.coerce.number())
  .refine((obj) => Object.keys(obj).length > 0);
export type DurationLikeObject = z.infer<typeof DurationLikeObject>;

export const DateRange = z.object({ start: DateTimeSchema, end: DateTimeSchema });
export type DateRange = z.infer<typeof DateRange>;

const durationSchema = z.custom<Duration>((val: unknown) => Duration.isDuration(val), {
  params: { name: 'Duration' },
});
const validDuration = durationSchema.refine(isValidDuration);
export const DurationSchema = z
  .union([durationSchema, DurationLikeObject.transform((obj) => Duration.fromObject(obj))])
  .pipe(durationSchema);

export const ValidDuration = DurationSchema.pipe(validDuration);
export type ValidDuration = Duration<true>;

export const DayOfWeek = z.enum(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']);
export type DayOfWeek = z.infer<typeof DayOfWeek>;
